(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';

  function CartDrawer(root) {
    this.root = root;
    this.busy = false;
    this.previouslyFocused = null;
    this.bind();
  }

  CartDrawer.prototype.bind = function () {
    var self = this;

    // data-cart-close — capture phase so we beat any third-party click handler
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.close();
      }
    }, true);

    // Delegated qty/remove handling stays scoped to the drawer
    this.root.addEventListener('click', function (e) {
      var itemEl = e.target.closest('[data-cart-item]');
      if (!itemEl) return;
      var key = itemEl.getAttribute('data-key');
      if (e.target.closest('[data-cart-remove]')) {
        e.preventDefault();
        self.changeQty(key, 0);
        return;
      }
      if (e.target.closest('[data-qty-increment]')) {
        e.preventDefault();
        var inc = parseInt(itemEl.querySelector('[data-qty-value]').textContent, 10) + 1;
        self.changeQty(key, inc);
        return;
      }
      if (e.target.closest('[data-qty-decrement]')) {
        e.preventDefault();
        var dec = parseInt(itemEl.querySelector('[data-qty-value]').textContent, 10) - 1;
        self.changeQty(key, Math.max(0, dec));
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen()) self.close();
    });
  };

  CartDrawer.prototype.isOpen = function () {
    return this.root.getAttribute('data-open') === 'true';
  };

  CartDrawer.prototype.open = function () {
    if (this.isOpen()) return;
    this.previouslyFocused = document.activeElement;
    this.root.setAttribute('data-open', 'true');
    this.root.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    var panel = this.root.querySelector('.cart-drawer__panel');
    requestAnimationFrame(function () { if (panel) panel.focus({ preventScroll: true }); });
  };

  CartDrawer.prototype.close = function () {
    if (!this.isOpen()) return;
    this.root.setAttribute('data-open', 'false');
    this.root.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (this.previouslyFocused && this.previouslyFocused.focus) {
      try { this.previouslyFocused.focus({ preventScroll: true }); } catch (_) {}
    }
  };

  CartDrawer.prototype.setBusy = function (busy) {
    this.busy = busy;
    this.root.setAttribute('data-busy', busy ? 'true' : 'false');
  };

  CartDrawer.prototype.addFromForm = function (form) {
    if (this.busy) return;
    this.setBusy(true);
    var submitBtn = form.querySelector('button[type="submit"], input[type="submit"], [data-add-to-cart]');
    if (submitBtn) submitBtn.disabled = true;
    var fd = new FormData(form);
    fd.append('sections', SECTION_ID);
    var self = this;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd,
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) return r.text().then(function (t) { throw new Error(t || 'add failed'); });
        return r.json();
      })
      .then(function (data) {
        self.applySections(data.sections);
        self.open();
      })
      .catch(function (err) {
        console.error('[cart-drawer] add failed:', err);
      })
      .finally(function () {
        self.setBusy(false);
        if (submitBtn) submitBtn.disabled = false;
      });
  };

  CartDrawer.prototype.changeQty = function (key, quantity) {
    if (this.busy) return;
    this.setBusy(true);
    var self = this;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION_ID }),
      credentials: 'same-origin',
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        self.applySections(data.sections);
      })
      .catch(function (err) { console.error('[cart-drawer] change failed:', err); })
      .finally(function () { self.setBusy(false); });
  };

  // Lightweight refresh that re-fetches the drawer section without mutating cart.
  CartDrawer.prototype.refresh = function () {
    var self = this;
    return fetch('/?sections=' + SECTION_ID, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) { return r.json(); })
      .then(function (sections) { self.applySections(sections); });
  };

  CartDrawer.prototype.applySections = function (sections) {
    if (!sections || !sections[SECTION_ID]) return;
    var doc = new DOMParser().parseFromString(sections[SECTION_ID], 'text/html');
    var fresh = doc.querySelector('[data-cart-drawer]');
    if (!fresh) return;

    this.morphBody(fresh);
    this.updateFooter(fresh);

    var newCount = fresh.getAttribute('data-cart-item-count');
    if (newCount != null) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = newCount;
      });
      this.root.setAttribute('data-cart-item-count', newCount);
    }
  };

  // Replace items in place where possible so images and the checkout button don't flicker.
  CartDrawer.prototype.morphBody = function (freshRoot) {
    var freshBody = freshRoot.querySelector('[data-cart-body]');
    var currentBody = this.root.querySelector('[data-cart-body]');
    if (!freshBody || !currentBody) return;

    var freshEmpty = freshBody.querySelector('[data-cart-empty]');
    var currentEmpty = currentBody.querySelector('[data-cart-empty]');

    // Transitions in/out of the empty state need a full swap.
    if (freshEmpty || currentEmpty) {
      if (freshEmpty && currentEmpty) return; // both empty, nothing to do
      currentBody.innerHTML = freshBody.innerHTML;
      return;
    }

    var freshList = freshBody.querySelector('[data-cart-items]');
    var currentList = currentBody.querySelector('[data-cart-items]');
    if (!freshList || !currentList) {
      currentBody.innerHTML = freshBody.innerHTML;
      return;
    }

    var currentByKey = {};
    currentList.querySelectorAll('[data-cart-item]').forEach(function (el) {
      currentByKey[el.getAttribute('data-key')] = el;
    });

    var freshItemEls = Array.from(freshList.querySelectorAll('[data-cart-item]'));
    var seen = {};
    var lastInsertedRef = null;

    freshItemEls.forEach(function (freshItem) {
      var key = freshItem.getAttribute('data-key');
      seen[key] = true;
      var existing = currentByKey[key];
      if (existing) {
        // Update qty + price text in place; leave the image alone.
        var newQty = freshItem.querySelector('[data-qty-value]');
        var oldQty = existing.querySelector('[data-qty-value]');
        if (newQty && oldQty && newQty.textContent !== oldQty.textContent) {
          oldQty.textContent = newQty.textContent;
        }
        var newPrice = freshItem.querySelector('.cart-drawer__item-price');
        var oldPrice = existing.querySelector('.cart-drawer__item-price');
        if (newPrice && oldPrice && newPrice.innerHTML !== oldPrice.innerHTML) {
          oldPrice.innerHTML = newPrice.innerHTML;
        }
        lastInsertedRef = existing;
      } else {
        var clone = freshItem.cloneNode(true);
        if (lastInsertedRef && lastInsertedRef.nextSibling) {
          currentList.insertBefore(clone, lastInsertedRef.nextSibling);
        } else {
          currentList.appendChild(clone);
        }
        lastInsertedRef = clone;
      }
    });

    Object.keys(currentByKey).forEach(function (key) {
      if (!seen[key]) currentByKey[key].remove();
    });
  };

  // Update footer totals + discounts without recreating the checkout button.
  CartDrawer.prototype.updateFooter = function (freshRoot) {
    var freshFooter = freshRoot.querySelector('[data-cart-footer]');
    var currentFooter = this.root.querySelector('[data-cart-footer]');
    if (!freshFooter || !currentFooter) return;

    currentFooter.hidden = freshFooter.hasAttribute('hidden');

    var freshTotal = freshFooter.querySelector('[data-cart-total]');
    var currentTotal = currentFooter.querySelector('[data-cart-total]');
    if (freshTotal && currentTotal && freshTotal.textContent !== currentTotal.textContent) {
      currentTotal.textContent = freshTotal.textContent;
    }

    var freshDisc = freshFooter.querySelector('[data-cart-discounts]');
    var currentDisc = currentFooter.querySelector('[data-cart-discounts]');
    if (freshDisc && currentDisc) {
      currentDisc.hidden = freshDisc.hasAttribute('hidden');
      if (freshDisc.innerHTML !== currentDisc.innerHTML) {
        currentDisc.innerHTML = freshDisc.innerHTML;
      }
    }
  };

  function init() {
    var root = document.querySelector('[data-cart-drawer]');
    if (!root) return;
    var drawer = new CartDrawer(root);
    window.cartDrawer = drawer;

    var pending = window.__cartDrawerPendingForms || [];
    window.__cartDrawerPendingForms = [];
    pending.forEach(function (f) { drawer.addFromForm(f); });
    if (window.__cartDrawerWantsOpen && !pending.length) {
      drawer.open();
    }
    window.__cartDrawerWantsOpen = false;

    // Fallback: if some other script (e.g., a Slide Cart leftover, quick-buy widget)
    // changes the cart, refresh our drawer and open it on increases.
    var headerCount = document.querySelector('.site-header [data-cart-count]');
    if (headerCount) {
      var lastCount = parseInt(headerCount.textContent, 10) || 0;
      new MutationObserver(function () {
        var newCount = parseInt(headerCount.textContent, 10) || 0;
        if (newCount === lastCount) return;
        var wasIncrease = newCount > lastCount;
        lastCount = newCount;
        drawer.refresh().then(function () {
          if (wasIncrease) drawer.open();
        });
      }).observe(headerCount, { childList: true, characterData: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
